/**
 * Conversational Rhythm Intelligence — Phase 1
 * ---------------------------------------------------------------
 * Behavioral cadence layer on top of the trajectory + timing
 * engines. Translates pressure / volatility / withdrawal /
 * posture history into a deterministic "rhythm state" — the
 * tempo and shape of the next exchange.
 *
 * This is NOT animation polish. Rhythm classifies HOW the
 * conversation breathes:
 *   - escalating_pressure   → fast bursts, minimal hesitation
 *   - passive_resistance    → delay → short jab → pause
 *   - unstable_withdrawal   → silence → brief reply → silence
 *   - compressed_conflict   → dense, overlapping, fast
 *   - silence_leverage      → long cold gaps, terse re-entry
 *   - emotional_flooding    → multi-fragment, rising volatility
 *   - detached_control      → flat, infrequent, cold
 *   - fragmented_escalation → typing → partial → pause → pivot
 *
 * Output is consumed by the Live Trajectory overlay, the
 * message timing engine (compression / expansion modifiers),
 * and downstream content / Remotion / replay tooling.
 */

import type {
  NextTurnStyle,
  PressureState,
  TrajectoryState,
} from "./behaviorRuntime";
import type { ForecastResponseMode } from "./forecastEngine";

export type RhythmType =
  | "escalating_pressure"
  | "passive_resistance"
  | "unstable_withdrawal"
  | "compressed_conflict"
  | "silence_leverage"
  | "emotional_flooding"
  | "detached_control"
  | "fragmented_escalation"
  | "stable_baseline";

export type PacingTempo =
  | "rapid"
  | "compressed"
  | "measured"
  | "delayed"
  | "sparse";

export type CadencePattern =
  | "burst_burst_spike"
  | "delay_jab_pause"
  | "silence_brief_silence"
  | "typing_partial_pivot"
  | "flat_terse"
  | "fragment_storm"
  | "steady_exchange";

export interface RhythmMemory {
  prevRhythm: RhythmType | null;
  firmRunLength: number;
  greyRockRunLength: number;
  softRunLength: number;
  /** Recent volatility samples (kept short for compression detection) */
  volatilityWindow: number[];
  /** Recent pressure samples */
  pressureWindow: number[];
  turn: number;
}

export const INITIAL_RHYTHM_MEMORY: RhythmMemory = {
  prevRhythm: null,
  firmRunLength: 0,
  greyRockRunLength: 0,
  softRunLength: 0,
  volatilityWindow: [],
  pressureWindow: [],
  turn: 0,
};

export interface RhythmInput {
  trajectoryState: TrajectoryState;
  pressureLevel?: number;
  volatility?: number;
  withdrawalLevel?: number;
  nextTurnStyle?: NextTurnStyle | null;
  responseMode: ForecastResponseMode;
  memory?: RhythmMemory | null;
}

export interface RhythmResult {
  rhythmType: RhythmType;
  rhythmLabel: string;
  pacingTempo: PacingTempo;
  cadencePattern: CadencePattern;
  /** 0–1 chance the agent interrupts/splits its own message */
  interruptionLikelihood: number;
  /** 0–100 — how far silence stretches between beats */
  silenceDrift: number;
  /** 0–100 — message fragmentation density */
  fragmentationLevel: number;
  /** 0–1 — compression multiplier (1.0 = neutral, <1 = faster, >1 = slower) */
  compressionFactor: number;
  /** Operational signal string for HUD */
  signal: string;
  memory: RhythmMemory;
}

const clamp = (n: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, n));
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const RHYTHM_LABELS: Record<RhythmType, string> = {
  escalating_pressure: "Escalating Pressure",
  passive_resistance: "Passive Resistance",
  unstable_withdrawal: "Unstable Withdrawal",
  compressed_conflict: "Compressed Conflict",
  silence_leverage: "Silence Leverage",
  emotional_flooding: "Emotional Flooding",
  detached_control: "Detached Control",
  fragmented_escalation: "Fragmented Escalation",
  stable_baseline: "Stable Baseline",
};

const RHYTHM_SIGNALS: Record<RhythmType, string> = {
  escalating_pressure: "Tempo accelerating — pressure compounding.",
  passive_resistance: "Resistance encoded in pacing gaps.",
  unstable_withdrawal: "Withdrawal cadence — re-entries cooling.",
  compressed_conflict: "Exchange compressed — friction density rising.",
  silence_leverage: "Silence weaponized as pressure.",
  emotional_flooding: "Volatility flooding the cadence.",
  detached_control: "Detached control — minimal affective signal.",
  fragmented_escalation: "Fragment cadence — pivots forming mid-thought.",
  stable_baseline: "Cadence steady.",
};

function pushWindow(arr: number[], v: number, max = 5): number[] {
  const next = [...arr, v];
  while (next.length > max) next.shift();
  return next;
}

function classifyRhythm(
  pressure: number,
  volatility: number,
  withdrawal: number,
  pressureTrend: TrajectoryState["pressureTrend"],
  pressureState: PressureState,
  style: NextTurnStyle | null | undefined,
  mode: ForecastResponseMode,
  mem: RhythmMemory,
): RhythmType {
  // Silence / withdrawal dominance
  if (style === "silence_pressure" || (withdrawal >= 70 && volatility < 45))
    return "silence_leverage";
  if (withdrawal >= 55 && pressureTrend !== "rising") return "unstable_withdrawal";
  if (mode === "minimal_grey_rock" && mem.greyRockRunLength >= 3 && volatility < 50)
    return "detached_control";

  // Flooding / fragmenting under volatility
  if (volatility >= 70 && pressure >= 55) return "emotional_flooding";
  if (
    style === "victim_framing" ||
    (volatility >= 55 && pressure >= 50 && pressureTrend === "rising")
  )
    return "fragmented_escalation";

  // Compression under sustained firm posture
  if (mem.firmRunLength >= 3 && pressure >= 55) return "compressed_conflict";

  // Escalation
  if (pressure >= 65 && pressureTrend === "rising") return "escalating_pressure";
  if (pressureState === "resistance_escalating") return "escalating_pressure";

  // Passive resistance
  if (
    style === "passive_withdrawal" ||
    style === "subtle_minimization" ||
    style === "tone_policing"
  )
    return "passive_resistance";

  return "stable_baseline";
}

const TEMPO_BY_RHYTHM: Record<RhythmType, PacingTempo> = {
  escalating_pressure: "rapid",
  compressed_conflict: "compressed",
  emotional_flooding: "rapid",
  fragmented_escalation: "compressed",
  passive_resistance: "delayed",
  unstable_withdrawal: "sparse",
  silence_leverage: "sparse",
  detached_control: "sparse",
  stable_baseline: "measured",
};

const CADENCE_BY_RHYTHM: Record<RhythmType, CadencePattern> = {
  escalating_pressure: "burst_burst_spike",
  compressed_conflict: "burst_burst_spike",
  emotional_flooding: "fragment_storm",
  fragmented_escalation: "typing_partial_pivot",
  passive_resistance: "delay_jab_pause",
  unstable_withdrawal: "silence_brief_silence",
  silence_leverage: "silence_brief_silence",
  detached_control: "flat_terse",
  stable_baseline: "steady_exchange",
};

/**
 * Primary export — deterministic rhythm read for the next turn.
 * Pass back `result.memory` on the next call to evolve cadence.
 */
export function getConversationRhythm(input: RhythmInput): RhythmResult {
  const t = input.trajectoryState;
  const pressure = clamp(input.pressureLevel ?? t.nextPressureLevel);
  const volatility = clamp(input.volatility ?? t.emotionalVolatility);
  const withdrawal = clamp(input.withdrawalLevel ?? t.withdrawalWeight);
  const style = input.nextTurnStyle ?? t.nextTurnStyle;
  const mode = input.responseMode;
  const prevMem = input.memory ?? INITIAL_RHYTHM_MEMORY;

  // Evolve posture run-lengths
  const firmRunLength =
    mode === "firm_boundary" ? prevMem.firmRunLength + 1 : 0;
  const greyRockRunLength =
    mode === "minimal_grey_rock" ? prevMem.greyRockRunLength + 1 : 0;
  const softRunLength =
    mode === "soft_neutral" ? prevMem.softRunLength + 1 : 0;

  const volatilityWindow = pushWindow(prevMem.volatilityWindow, volatility);
  const pressureWindow = pushWindow(prevMem.pressureWindow, pressure);

  const memForClassify: RhythmMemory = {
    ...prevMem,
    firmRunLength,
    greyRockRunLength,
    softRunLength,
  };

  const rhythmType = classifyRhythm(
    pressure,
    volatility,
    withdrawal,
    t.pressureTrend,
    t.pressureState,
    style,
    mode,
    memForClassify,
  );

  // Compression: <1 = faster cadence, >1 = slower / sparser.
  // High pressure + escalation → compress (~0.65). Withdrawal → expand (~1.7).
  let compressionFactor = 1;
  switch (rhythmType) {
    case "escalating_pressure":   compressionFactor = 0.72; break;
    case "compressed_conflict":   compressionFactor = 0.62; break;
    case "emotional_flooding":    compressionFactor = 0.7;  break;
    case "fragmented_escalation": compressionFactor = 0.85; break;
    case "passive_resistance":    compressionFactor = 1.25; break;
    case "unstable_withdrawal":   compressionFactor = 1.55; break;
    case "silence_leverage":      compressionFactor = 1.75; break;
    case "detached_control":      compressionFactor = 1.45; break;
    case "stable_baseline":       compressionFactor = 1.0;  break;
  }
  // Volatility nudges compression — chaotic states tighten cadence.
  compressionFactor *= 1 - (volatility - 50) / 320;
  compressionFactor = Math.max(0.5, Math.min(1.95, compressionFactor));

  // Interruption likelihood
  let interruptionLikelihood = 0.15;
  if (rhythmType === "fragmented_escalation") interruptionLikelihood = 0.6;
  else if (rhythmType === "emotional_flooding") interruptionLikelihood = 0.7;
  else if (rhythmType === "compressed_conflict") interruptionLikelihood = 0.45;
  else if (rhythmType === "escalating_pressure") interruptionLikelihood = 0.35;
  else if (rhythmType === "passive_resistance") interruptionLikelihood = 0.25;
  interruptionLikelihood = clamp01(
    interruptionLikelihood + (volatility - 50) / 220,
  );

  // Silence drift (0–100)
  let silenceDrift = 30;
  if (rhythmType === "silence_leverage") silenceDrift = 90;
  else if (rhythmType === "unstable_withdrawal") silenceDrift = 75;
  else if (rhythmType === "detached_control") silenceDrift = 65;
  else if (rhythmType === "passive_resistance") silenceDrift = 55;
  else if (rhythmType === "compressed_conflict") silenceDrift = 15;
  else if (rhythmType === "escalating_pressure") silenceDrift = 18;
  silenceDrift = clamp(silenceDrift + (withdrawal - 40) / 4);

  // Fragmentation level (0–100)
  let fragmentationLevel = 20;
  if (rhythmType === "fragmented_escalation") fragmentationLevel = 80;
  else if (rhythmType === "emotional_flooding") fragmentationLevel = 90;
  else if (rhythmType === "compressed_conflict") fragmentationLevel = 65;
  else if (rhythmType === "escalating_pressure") fragmentationLevel = 50;
  fragmentationLevel = clamp(
    fragmentationLevel + (volatility - 50) / 4,
  );

  const memory: RhythmMemory = {
    prevRhythm: rhythmType,
    firmRunLength,
    greyRockRunLength,
    softRunLength,
    volatilityWindow,
    pressureWindow,
    turn: prevMem.turn + 1,
  };

  return {
    rhythmType,
    rhythmLabel: RHYTHM_LABELS[rhythmType],
    pacingTempo: TEMPO_BY_RHYTHM[rhythmType],
    cadencePattern: CADENCE_BY_RHYTHM[rhythmType],
    interruptionLikelihood,
    silenceDrift,
    fragmentationLevel,
    compressionFactor,
    signal: RHYTHM_SIGNALS[rhythmType],
    memory,
  };
}

/**
 * Apply rhythm modifiers to a base TimingProfile-like object.
 * Compresses / expands delays without overriding the underlying
 * style cadence chosen by the message timing engine.
 */
export function applyRhythmToTiming<
  T extends {
    responseDelayMs: number;
    typingDelayMs: number;
    fragmentGapMs: number;
    hesitationProbability: number;
    fragmented: boolean;
  },
>(timing: T, rhythm: RhythmResult): T {
  const c = rhythm.compressionFactor;
  const fragmentBoost = rhythm.fragmentationLevel >= 60;
  return {
    ...timing,
    responseDelayMs: Math.round(
      Math.max(280, Math.min(7800, timing.responseDelayMs * c)),
    ),
    typingDelayMs: Math.round(
      Math.max(420, Math.min(4600, timing.typingDelayMs * (0.7 + c * 0.3))),
    ),
    fragmentGapMs: Math.round(
      Math.max(500, Math.min(2200, timing.fragmentGapMs * c)),
    ),
    hesitationProbability: Math.max(
      timing.hesitationProbability,
      rhythm.interruptionLikelihood * 0.85,
    ),
    fragmented: timing.fragmented || fragmentBoost,
  };
}
